"use client";
import React, { createContext, useContext, useCallback, useState } from "react";
import {
  FlowInterface,
  ModelInterface,
  DatabaseType,
  MetaDataInterface,
  StorageType,
  FunctionInterface,
} from "@/interfaces";
import { NodeContextProvider, useNodes } from "@/context/NodeContext";
import { TaskContextProvider, useTasks } from "@/context/TaskContext";
import { useModal } from "@/context/ModalContext";
import { extractSRS, loadJsonModule } from "@/utils/flow/flowUtils";
import { MetaDataContextProvider, useMetaData } from "./MetadataContext";
import { ProjectState } from "@/interfaces/contextInterfaces/ProjectState";
import { StateManagerContext } from "@/context/StateManagerContext";
import { CommandAction } from "@/interfaces/stateManagement/CommandInterfaces";
import { useCodeGenerationLoading } from "@/context/CodeGenerationLoadingContext";

// ==================== TYPES ====================

interface ConfirmDialogState {
  open: boolean;
  message: string;
  resolver: (value: boolean) => void;
}

// ==================== CONTEXT ====================

const FlowContext = createContext<FlowInterface | undefined>(undefined);

// ==================== HELPER FUNCTIONS ====================

const isArrayReference = (p: any) =>
  p?.type === "Reference" && (p?.isArray === true || Array.isArray(p?.schema));

const hasManyToManyRelationship = (modelNodes: any[]) =>
  modelNodes.some((a) =>
    modelNodes.some(
      (b) =>
        a.id !== b.id &&
        (a.data?.properties || []).some(
          (pa: any) =>
            isArrayReference(pa) &&
            (pa.reference || "").toLowerCase() ===
            (b.data?.name || "").toLowerCase()
        ) &&
        (b.data?.properties || []).some(
          (pb: any) =>
            isArrayReference(pb) &&
            (pb.reference || "").toLowerCase() ===
            (a.data?.name || "").toLowerCase()
        )
    )
  );

// ==================== INNER PROVIDER ====================

const FlowContextInner: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const modal = useModal();
  const nodeContext = useNodes();
  const taskContext = useTasks();
  const metaDataContext = useMetaData();
  const { showLoading, hideLoading } = useCodeGenerationLoading();

  // Safely access StateManagerContext (returns undefined if not available)
  const stateManagerContext = useContext(StateManagerContext);

  const [confirmState, setConfirmState] = useState<ConfirmDialogState>({
    open: false,
    message: "",
    resolver: () => { },
  });

  // ==================== CONFIRM DIALOG ====================

  const openConfirm = useCallback((message: string): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      setConfirmState({ open: true, message, resolver: resolve });
    });
  }, []);

  const resolveConfirm = useCallback(
    (value: boolean) => {
      confirmState.resolver(value);
      setConfirmState((prev) => ({ ...prev, open: false }));
    },
    [confirmState.resolver]
  );

  // ==================== OPERATIONS ====================

  const deleteModel = useCallback(
    async (modelId: string) => {
      await nodeContext.deleteModel(modelId, openConfirm);
    },
    [nodeContext, openConfirm]
  );

  const generateSchemaFromTemplate = async (id: string = "Instagram") => {
    try {
      console.log(`Loading schema with ID: ${id}`);
      const { models, metadata } = loadJsonModule(id);

      if (!models || models.length === 0) {
        console.warn(
          `No models found for ID ${id}. Aborting schema generation.`
        );
        return;
      }

      nodeContext.setNodesFromModels(models);
      metaDataContext.setMetaData(metadata);
      console.log(`Schema generation complete for ID ${id}.`);
    } catch (error) {
      console.error("Schema generation failed.");
    }
  };

  const extractTasksFromSRS = useCallback(
    async (srs: string) => {
      taskContext.setIsExtracting(true);
      taskContext.setExtractionError(null);

      try {
        const result = await extractSRS(srs);
        taskContext.setExtractedTasks(result.tasks);

        if (result.models) {
          nodeContext.setNodesFromModels(result.models);
        }

        metaDataContext.setMetaData(result.metadata);

        // Add backend entities to state manager if available
        if (result.backendState && stateManagerContext) {
          console.log(`[FlowContext] Adding backend entities to state manager: ${result.backendState.services?.length || 0} services, ${result.backendState.endpoints?.length || 0} endpoints, ${result.backendState.middlewares?.length || 0} middlewares, ${result.backendState.functions?.length || 0} functions`);

          // Collect all functions including those in services' serviceFunctions
          const allFunctions = new Map<string, FunctionInterface>();

          // Add standalone functions
          if (result.backendState.functions && result.backendState.functions.length > 0) {
            result.backendState.functions.forEach(func => {
              allFunctions.set(func.id, func);
            });
          }

          // Extract functions from services' serviceFunctions
          if (result.backendState.services && result.backendState.services.length > 0) {
            result.backendState.services.forEach(service => {
              if (service.serviceFunctions && service.serviceFunctions.length > 0) {
                service.serviceFunctions.forEach(func => {
                  if (!allFunctions.has(func.id)) {
                    allFunctions.set(func.id, func);
                  }
                });
              }
            });
          }

          // Add all functions first (they may be referenced by services, endpoints, middlewares)
          for (const func of allFunctions.values()) {
            try {
              await stateManagerContext.executeCommand({
                id: `cmd-add-function-${func.id}`,
                action: CommandAction.ADD_FUNCTION,
                target: { functionId: func.id },
                functionObject: func,
                timestamp: new Date().toISOString()
              });
            } catch (error) {
              console.warn(`[FlowContext] Failed to add function ${func.id}:`, error);
            }
          }

          // Add services
          if (result.backendState.services && result.backendState.services.length > 0) {
            for (const service of result.backendState.services) {
              try {
                await stateManagerContext.executeCommand({
                  id: `cmd-add-service-${service.id}`,
                  action: CommandAction.ADD_SERVICE,
                  target: { service: service.id },
                  data: { service },
                  timestamp: new Date().toISOString()
                });
              } catch (error) {
                console.warn(`[FlowContext] Failed to add service ${service.id}:`, error);
              }
            }
          }

          // Add middlewares
          if (result.backendState.middlewares && result.backendState.middlewares.length > 0) {
            for (const middleware of result.backendState.middlewares) {
              try {
                await stateManagerContext.executeCommand({
                  id: `cmd-add-middleware-${middleware.id}`,
                  action: CommandAction.ADD_MIDDLEWARE,
                  target: { middleware: middleware.id },
                  data: { middleware },
                  timestamp: new Date().toISOString()
                });
              } catch (error) {
                console.warn(`[FlowContext] Failed to add middleware ${middleware.id}:`, error);
              }
            }
          }

          // Add endpoints (they may reference services, middlewares, functions)
          if (result.backendState.endpoints && result.backendState.endpoints.length > 0) {
            for (const endpoint of result.backendState.endpoints) {
              try {
                await stateManagerContext.executeCommand({
                  id: `cmd-add-endpoint-${endpoint.id}`,
                  action: CommandAction.ADD_ENDPOINT,
                  target: {
                    endpoint: {
                      name: endpoint.name,
                      method: endpoint.method,
                      path: endpoint.path
                    }
                  },
                  data: { endpoint },
                  functionObject: endpoint.functionId ? result.backendState.functions?.find(f => f.id === endpoint.functionId) : undefined,
                  timestamp: new Date().toISOString()
                });
              } catch (error) {
                console.warn(`[FlowContext] Failed to add endpoint ${endpoint.id}:`, error);
              }
            }
          }

          console.log(`[FlowContext] Successfully added backend entities to state manager`);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error";
        taskContext.setExtractionError(errorMessage);
        console.error("Error extracting tasks:", error);
      } finally {
        taskContext.setIsExtracting(false);
      }
    },
    [nodeContext, taskContext, metaDataContext, stateManagerContext]
  );

  const generateCodeFromTasks = useCallback(async (projectId?: string) => {
    taskContext.setIsGenerating(true);
    taskContext.selectAllTasks(true);
    const selectedTasksList = taskContext.getSelectedTasksList();

    try {
      // MongoDB many-to-many check
      if (
        metaDataContext.metaData.databaseType === DatabaseType.MONGODB &&
        hasManyToManyRelationship(nodeContext.modelNodes)
      ) {
        const proceed = await modal.confirm(
          "Many-to-many detected",
          "MongoDB does not natively support many-to-many arrays on both sides. You may need a join collection. Do you want to continue?"
        );
        if (!proceed) {
          taskContext.setIsGenerating(false);
          return;
        }
      }

      const models = nodeContext.getModelsFromNodes();
      const effectiveTasks =
        selectedTasksList?.length > 0
          ? selectedTasksList
          : taskContext.getSelectedTasksList();

      // Get current backend state (if available)
      const hasStateManager = !!stateManagerContext;
      const systemState = stateManagerContext?.systemState;

      const backendStatePayload = systemState ? {
        services: systemState.services || [],
        middlewares: systemState.middlewares || [],
        endpoints: systemState.endpoints || [],
        functions: systemState.functions || [],
      } : {
        services: [],
        middlewares: [],
        endpoints: [],
        functions: [],
      };

      console.log("[Frontend] generateCode payload", {
        modelsCount: models.length,
        metaDb: metaDataContext.metaData?.databaseType,
        tasksCount: effectiveTasks.length,
        taskIdsSample: effectiveTasks.map((t) => t.taskInterfaceID),
        hasStateManager,
        systemStateAvailable: !!systemState,
        backendServicesCount: backendStatePayload.services.length,
        backendMiddlewaresCount: backendStatePayload.middlewares.length,
        backendEndpointsCount: backendStatePayload.endpoints.length,
        backendFunctionsCount: backendStatePayload.functions.length,
        projectId,
        backendStateDetails: systemState ? {
          services: systemState.services.map(s => ({ id: s.id, name: s.serviceName })),
          middlewares: systemState.middlewares.map(m => ({ id: m.id, name: m.middlewareName })),
          endpoints: systemState.endpoints.map(e => ({ id: e.id, name: e.name, path: e.path })),
          functions: systemState.functions.map(f => ({ id: f.id, name: f.functionName })),
        } : null,
      });

      // Show loading overlay
      showLoading();

      const response = await fetch("/api/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          models,
          metaData: metaDataContext.metaData,
          tasks: effectiveTasks,
          backendState: backendStatePayload,
          projectId,
        }),
      });

      if (!response.ok) throw new Error("Failed to generate code");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "generated-code.zip";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error generating code:", error);
      taskContext.setExtractionError("Failed to generate code");
      modal.showError(
        "Generation failed",
        "We were unable to generate code. Please try again."
      );
    } finally {
      taskContext.setIsGenerating(false);
      hideLoading();
    }
  }, [nodeContext, taskContext, metaDataContext, modal, stateManagerContext, showLoading, hideLoading]);

  const clearExtractedData = useCallback(() => {
    taskContext.clearExtractedTasks();
  }, [taskContext]);

  const loadProjectState = useCallback(
    (projectState: ProjectState) => {
      // Clear everything first
      nodeContext.clearNodes();
      taskContext.clearExtractedTasks();
      metaDataContext.resetMetaData();

      // Load data
      if (projectState.models?.length > 0) {
        nodeContext.setNodesFromModels(projectState.models);
      }
      if (projectState.metadata) {
        metaDataContext.setMetaData(projectState.metadata);
      }
      if (projectState.tasks) {
        taskContext.setExtractedTasks(projectState.tasks);
      }
    },
    [nodeContext, taskContext, metaDataContext]
  );

  const clearWorkspaceState = useCallback(() => {
    nodeContext.clearNodes();
    taskContext.clearExtractedTasks();
    metaDataContext.resetMetaData();
  }, [nodeContext, taskContext, metaDataContext]);

  // --- Unsaved Changes Logic ---
  const [isChanged, setIsChanged] = useState(false);
  // We need a ref to track the last saved state to avoid dependency loops or stale closures
  // However, since we need to compare deep objects, stringifying is a simple approach
  const lastSavedState = React.useRef<string>("");

  // Initialize lastSavedState when data is loaded
  React.useEffect(() => {
    if (nodeContext.modelNodes.length > 0 && lastSavedState.current === "") {
      const currentState = JSON.stringify({
        models: nodeContext.modelNodes.map(node => ({
          name: node.data.name,
          properties: node.data.properties,
          options: node.data.options,
          ui: { position: node.position }
        })),
        metaData: metaDataContext.metaData
      });
      lastSavedState.current = currentState;
    }
  }, [nodeContext.modelNodes, metaDataContext.metaData]);

  // Check for changes
  React.useEffect(() => {
    // If we haven't established a baseline, don't mark as changed yet
    // unless we have nodes and no baseline (which is handled above)
    if (lastSavedState.current === "") return;

    const currentState = JSON.stringify({
      models: nodeContext.modelNodes.map(node => ({
        name: node.data.name,
        properties: node.data.properties,
        options: node.data.options,
        ui: { position: node.position }
      })),
      metaData: metaDataContext.metaData
    });

    setIsChanged(currentState !== lastSavedState.current);
  }, [nodeContext.modelNodes, metaDataContext.metaData]);

  // Warn on exit
  React.useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isChanged) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isChanged]);

  const saveProject = async () => {
    // Here you would typically make an API call to save the data
    // For now, we just update the lastSavedState
    const currentState = JSON.stringify({
      models: nodeContext.modelNodes.map(node => ({
        name: node.data.name,
        properties: node.data.properties,
        options: node.data.options,
        ui: { position: node.position }
      })),
      metaData: metaDataContext.metaData
    });
    lastSavedState.current = currentState;
    setIsChanged(false);
  };

  // ==================== CONTEXT VALUE ====================

  const {
    addNode,
    removeNode,
    addEdge,
    removeEdge,
    onConnect,
    bulkAddNodes,
    addAuthentication,
    addRoleModel,
    setNodesFromModels,
    clearNodes,
    getModelsFromNodes,
    alignNodes,
  } = nodeContext;

  const contextValue: FlowInterface = {
    // State
    modelNodes: nodeContext.modelNodes,
    edges: nodeContext.edges,
    metaData: metaDataContext.metaData,
    extractedTasks: taskContext.extractedTasks,
    extractedModels: nodeContext.getModelsFromNodes(),
    selectedTasks: taskContext.selectedTasks,
    isExtracting: taskContext.isExtracting,
    isGenerating: taskContext.isGenerating,
    extractionError: taskContext.extractionError,

    // Node operations
    onNodesChange: nodeContext.onNodesChange,
    addNode,
    removeNode,
    deleteModel,

    // Edge operations
    addEdge,
    removeEdge,
    onEdgesChange: nodeContext.onEdgesChange,
    onConnect,

    // Metadata operations
    updateMetaData: metaDataContext.updateMetaData,
    addAuthentication,
    addRoleModel,
    generateSchemaFromTemplate,

    // Task operations
    selectTask: taskContext.selectTask,
    selectAllTasks: taskContext.selectAllTasks,
    addCustomTask: taskContext.addCustomTask,
    removeTask: taskContext.removeTask,

    // Integrated operations
    extractTasksFromSRS,
    generateCodeFromTasks,
    clearExtractedData,
    loadProjectState,
    clearWorkspaceState,
    alignNodes,
    saveProject,
    isChanged,
  };

  return (
    <FlowContext.Provider value={contextValue}>
      {children}

      {/* Confirm Dialog */}
      {confirmState.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => resolveConfirm(false)}
          />
          <div className="relative z-10 w-full max-w-md mx-4 rounded-2xl border border-slate-700 bg-slate-900 text-slate-100 shadow-2xl">
            <div className="px-5 py-4">
              <h3 className="text-lg font-semibold mb-2">Confirm action</h3>
              <p className="text-slate-300 mb-4 whitespace-pre-line">
                {confirmState.message}
              </p>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => resolveConfirm(false)}
                  className="px-3 py-1.5 rounded-md bg-slate-700 hover:bg-slate-600 text-slate-100 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={() => resolveConfirm(true)}
                  className="px-3 py-1.5 rounded-md bg-rose-600 hover:bg-rose-700 text-[var(--primaryText)] transition"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </FlowContext.Provider>
  );
};

// ==================== OUTER PROVIDER ====================

export const FlowContextProvider: React.FC<{
  children: React.ReactNode;
  initialState?: ProjectState;
}> = ({ children, initialState }) => {
  const modal = useModal();

  return (
    <MetaDataContextProvider initialMetaData={initialState?.metadata}>
      <TaskContextProvider initialTasks={initialState?.tasks}>
        <NodeContextProvider
          onError={modal.showError}
          onSuccess={modal.showSuccess}
        >
          <FlowContextInner>{children}</FlowContextInner>
        </NodeContextProvider>
      </TaskContextProvider>
    </MetaDataContextProvider>
  );
};

// ==================== HOOK ====================

export const useFlow = () => {
  const context = useContext(FlowContext);
  if (!context) {
    throw new Error("useFlow must be used within FlowContextProvider");
  }
  return context;
};

export default FlowContext;
